import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Checkbox } from '@/components/shadcn/checkbox';
import { Button } from '@/components/shadcn/button';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/services/authService';
import showToast from '@/components/ui/Toast';
import { openExternalUrl } from '@/utils/openExternalUrl';
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from '@/components/auth/constants';

const ACKNOWLEDGEMENTS = [
  'Akses ke Sporto berakhir secara permanen.',
  'Saldo, poin, dan promo yang tersisa akan hangus.',
  'Pengajuan hanya jika tidak ada booking lapangan berjalan; saya menyatakan transaksi aktif sudah selesai.',
  'Saya setuju verifikasi identitas tambahan bila diperlukan.',
];

interface DeleteAccountPageProps {
  backTo: string;
}

export function DeleteAccountPage({ backTo }: DeleteAccountPageProps) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [checks, setChecks] = useState([false, false, false, false]);
  const [submitting, setSubmitting] = useState(false);

  const allChecked = checks.every(Boolean);

  const toggle = (index: number) => {
    setChecks((prev) => prev.map((value, i) => (i === index ? !value : value)));
  };

  const handleSubmit = async () => {
    if (!allChecked || submitting) {
      return;
    }
    setSubmitting(true);
    try {
      await authService.deleteAccount();
      await logout();
      navigate('/', { replace: true });
      showToast.success('Akun berhasil dihapus');
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Gagal menghapus akun';
      showToast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 pt-4 pb-8 space-y-5">
      <div className="hidden md:flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(backTo)}
          className="w-9 h-9 rounded-full bg-white border border-[#E0E6ED] flex items-center justify-center"
          aria-label="Kembali"
        >
          <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
        </button>
        <h1 className="text-lg font-bold text-[#0E1726]">Hapus Akun</h1>
      </div>

      <p className="text-sm text-gray-500 leading-relaxed">
        Menghapus akun bersifat permanen. Baca dan centang semua pernyataan di bawah, serta tinjau{' '}
        <button
          type="button"
          className="text-[#FC480D] underline"
          onClick={() => void openExternalUrl(TERMS_OF_SERVICE_URL)}
        >
          Syarat & Ketentuan
        </button>{' '}
        dan{' '}
        <button
          type="button"
          className="text-[#FC480D] underline"
          onClick={() => void openExternalUrl(PRIVACY_POLICY_URL)}
        >
          Kebijakan Privasi
        </button>
        .
      </p>

      <div className="space-y-3">
        {ACKNOWLEDGEMENTS.map((text, index) => (
          <label
            key={text}
            className="flex items-start gap-3 rounded-2xl border border-gray-100 bg-white p-3"
          >
            <Checkbox
              checked={checks[index]}
              onCheckedChange={() => toggle(index)}
              aria-label={text}
            />
            <span className="text-sm text-[#0E1726] leading-snug">{text}</span>
          </label>
        ))}
      </div>

      <Button
        type="button"
        disabled={!allChecked || submitting}
        onClick={() => void handleSubmit()}
        className="w-full h-12 rounded-2xl bg-red-600 hover:bg-red-700 text-white font-bold disabled:opacity-40 active:scale-[0.98]"
      >
        {submitting ? 'Menghapus…' : 'Hapus Akun'}
      </Button>
    </div>
  );
}

export default DeleteAccountPage;
